import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  buildCommerceAuditColumns,
  formatCommerceDateOnly,
  useCommerceUserTimezone,
} from '../utils/commerceListDateUtils';
import { useLanguage } from '../contexts/LanguageContext';
import { commerceServerTablePagination, COMMERCE_DEFAULT_PAGE_SIZE } from '../constants/commerceListPagination';
import { getUserPrimaryCompanyId, hasRole } from '../utils/permissionUtils';
import {
  parseCustomerNameLocalesRoot,
  resolveCustomerDisplayName,
  resolveLocalizedEntityName
} from '../constants/commerceNameLocales';

const { Title, Text } = Typography;

const pick = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== '') ?? null;

const CommercePriceCardsPage = () => {
  const { t, currentLanguage } = useLanguage();
  const userTimezoneOffset = useCommerceUserTimezone();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [priceCards, setPriceCards] = useState([]);
  const [priceCardsTotal, setPriceCardsTotal] = useState(0);
  const [listPage, setListPage] = useState(1);
  const [listPageSize, setListPageSize] = useState(COMMERCE_DEFAULT_PAGE_SIZE);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [companiesForSelect, setCompaniesForSelect] = useState([]);
  const [companyFilter, setCompanyFilter] = useState(() => getUserPrimaryCompanyId());
  const [customerNoFilter, setCustomerNoFilter] = useState('');
  const [customerNameFilter, setCustomerNameFilter] = useState('');
  const [branchNoFilter, setBranchNoFilter] = useState('');
  const [itemNoFilter, setItemNoFilter] = useState('');
  const [customerNoInput, setCustomerNoInput] = useState('');
  const [customerNameInput, setCustomerNameInput] = useState('');
  const [branchNoInput, setBranchNoInput] = useState('');
  const [itemNoInput, setItemNoInput] = useState('');
  const [form] = Form.useForm();
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [customerPickerLoading, setCustomerPickerLoading] = useState(false);
  const [customerPickerSearchInput, setCustomerPickerSearchInput] = useState('');
  const [customerPickerKeyword, setCustomerPickerKeyword] = useState('');
  const [customerPickerPage, setCustomerPickerPage] = useState(1);
  const [customerPickerPageSize, setCustomerPickerPageSize] = useState(20);
  const [customerPickerTotal, setCustomerPickerTotal] = useState(0);
  const [customerPickerRows, setCustomerPickerRows] = useState([]);
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [productPickerLoading, setProductPickerLoading] = useState(false);
  const [productPickerSearchInput, setProductPickerSearchInput] = useState('');
  const [productPickerKeyword, setProductPickerKeyword] = useState('');
  const [productPickerPage, setProductPickerPage] = useState(1);
  const [productPickerPageSize, setProductPickerPageSize] = useState(20);
  const [productPickerTotal, setProductPickerTotal] = useState(0);
  const [productPickerRows, setProductPickerRows] = useState([]);
  const [productPreview, setProductPreview] = useState(null);

  const userInfo = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('userInfo') || '{}') : {};
  const isTenantAdmin = hasRole(userInfo, 'Tenant_Admin');
  const watchedCompanyId = Form.useWatch('companyId', form);
  const watchedItemNo = Form.useWatch('itemNo', form);

  const fetchWithAuth = useCallback((url, options = {}) => {
    const token = localStorage.getItem('token');
    const headers = { ...(options.headers || {}) };
    const isFormData = options.body instanceof FormData;
    if (!isFormData && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    if (token) headers.Authorization = `Bearer ${token}`;
    headers['X-UI-Locale'] = currentLanguage || 'zh-TC';
    return fetch(url, { ...options, headers });
  }, [currentLanguage]);

  const extractResponseError = async (response) => {
    const text = await response.text();
    if (!text) return `HTTP ${response.status}`;
    try {
      const data = JSON.parse(text);
      return data?.error || data?.detail || data?.message || text;
    } catch {
      return text;
    }
  };

  const loadPriceCards = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (companyFilter) params.set('companyId', companyFilter);
      if (customerNoFilter.trim()) params.set('customerNo', customerNoFilter.trim());
      if (customerNameFilter.trim()) params.set('customerName', customerNameFilter.trim());
      if (branchNoFilter.trim()) params.set('branchNo', branchNoFilter.trim());
      if (itemNoFilter.trim()) params.set('itemNo', itemNoFilter.trim());
      params.set('page', String(listPage));
      params.set('pageSize', String(listPageSize));
      const response = await fetchWithAuth(`/api/commerce/price-cards?${params.toString()}`);
      if (!response.ok) throw new Error(await extractResponseError(response));
      const data = await response.json();
      const items = Array.isArray(data?.items) ? data.items : [];
      const total = typeof data?.total === 'number' ? data.total : items.length;
      setPriceCards(items);
      setPriceCardsTotal(total);
    } catch (err) {
      message.error(`${t('commercePriceCards.loadFailed')}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, companyFilter, customerNoFilter, customerNameFilter, branchNoFilter, itemNoFilter, listPage, listPageSize, t]);

  const loadCompaniesForSelect = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/api/commerce/companies-for-select');
      if (!res.ok) return;
      const data = await res.json();
      setCompaniesForSelect(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn('載入公司列表失敗:', err);
    }
  }, [fetchWithAuth]);

  const commitSearch = useCallback(() => {
    setCustomerNoFilter(customerNoInput);
    setCustomerNameFilter(customerNameInput);
    setBranchNoFilter(branchNoInput);
    setItemNoFilter(itemNoInput);
    setListPage(1);
  }, [customerNoInput, customerNameInput, branchNoInput, itemNoInput]);

  useEffect(() => {
    loadPriceCards();
  }, [loadPriceCards]);

  useEffect(() => {
    loadCompaniesForSelect();
  }, [loadCompaniesForSelect]);

  const loadProductPreviewByItemNo = useCallback(async (itemNo, companyIdForProduct) => {
    if (!itemNo || !String(itemNo).trim()) {
      setProductPreview(null);
      return;
    }
    try {
      const params = new URLSearchParams();
      params.set('search', String(itemNo).trim());
      params.set('page', '1');
      params.set('pageSize', '40');
      if (isTenantAdmin && companyIdForProduct) params.set('companyId', String(companyIdForProduct));
      const res = await fetchWithAuth(`/api/commerce/products?${params.toString()}`);
      if (!res.ok) return;
      const data = await res.json();
      const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      const target = String(itemNo).trim();
      const match = items.find((p) => String(pick(p.productRetailerId, p.productRetailer_id)) === target);
      if (match) {
        setProductPreview({
          retailerId: target,
          name: match.name || '',
          price: match.price != null ? Number(match.price) : null,
          currency: match.currency || 'HKD'
        });
      } else {
        setProductPreview(null);
      }
    } catch {
      setProductPreview(null);
    }
  }, [fetchWithAuth, isTenantAdmin]);

  const fetchPickerCustomers = useCallback(async () => {
    setCustomerPickerLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(customerPickerPage));
      params.set('pageSize', String(customerPickerPageSize));
      if (customerPickerKeyword.trim()) params.set('search', customerPickerKeyword.trim());
      if (isTenantAdmin && watchedCompanyId) params.set('companyId', String(watchedCompanyId));
      const res = await fetchWithAuth(`/api/commerce/customers-for-select?${params.toString()}`);
      if (!res.ok) throw new Error(await extractResponseError(res));
      const data = await res.json();
      const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      const total = typeof data?.total === 'number' ? data.total : items.length;
      setCustomerPickerRows(items);
      setCustomerPickerTotal(total);
    } catch (err) {
      message.error(`${t('commercePriceCards.customerPickerLoadFailed')}: ${err.message}`);
      setCustomerPickerRows([]);
      setCustomerPickerTotal(0);
    } finally {
      setCustomerPickerLoading(false);
    }
  }, [fetchWithAuth, customerPickerPage, customerPickerPageSize, customerPickerKeyword, isTenantAdmin, watchedCompanyId, t]);

  useEffect(() => {
    if (!customerPickerOpen) return;
    fetchPickerCustomers();
  }, [customerPickerOpen, fetchPickerCustomers]);

  const fetchPickerProducts = useCallback(async () => {
    setProductPickerLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(productPickerPage));
      params.set('pageSize', String(productPickerPageSize));
      if (productPickerKeyword.trim()) params.set('search', productPickerKeyword.trim());
      if (isTenantAdmin && watchedCompanyId) params.set('companyId', String(watchedCompanyId));
      const res = await fetchWithAuth(`/api/commerce/products?${params.toString()}`);
      if (!res.ok) throw new Error(await extractResponseError(res));
      const data = await res.json();
      const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      const total = typeof data?.total === 'number' ? data.total : items.length;
      setProductPickerRows(items);
      setProductPickerTotal(total);
    } catch (err) {
      message.error(`${t('commercePriceCards.productPickerLoadFailed')}: ${err.message}`);
      setProductPickerRows([]);
      setProductPickerTotal(0);
    } finally {
      setProductPickerLoading(false);
    }
  }, [fetchWithAuth, productPickerPage, productPickerPageSize, productPickerKeyword, isTenantAdmin, watchedCompanyId, t]);

  useEffect(() => {
    if (!productPickerOpen) return;
    fetchPickerProducts();
  }, [productPickerOpen, fetchPickerProducts]);

  const openCustomerPicker = () => {
    if (isTenantAdmin && !watchedCompanyId) {
      message.warning(t('commercePriceCards.selectCompanyFirst'));
      return;
    }
    setCustomerPickerPage(1);
    setCustomerPickerKeyword('');
    setCustomerPickerSearchInput('');
    setCustomerPickerOpen(true);
  };

  const openProductPicker = () => {
    if (isTenantAdmin && !watchedCompanyId) {
      message.warning(t('commercePriceCards.selectCompanyFirst'));
      return;
    }
    setProductPickerPage(1);
    setProductPickerKeyword('');
    setProductPickerSearchInput('');
    setProductPickerOpen(true);
  };

  const applyPickedCustomer = useCallback((row) => {
    const cn = pick(row.customerNo, row.customer_no);
    const bn = pick(row.branchNo, row.branch_no) ?? '';
    if (!cn) {
      message.error(t('commercePriceCards.required'));
      return;
    }
    form.setFieldsValue({ customerNo: String(cn), branchNo: String(bn) });
    setCustomerPickerOpen(false);
  }, [form, t]);

  const applyPickedProduct = useCallback((row) => {
    const rid = pick(row.productRetailerId, row.productRetailer_id);
    if (!rid) {
      message.error(t('commercePriceCards.required'));
      return;
    }
    const listPrice = row.price != null ? Number(row.price) : null;
    form.setFieldsValue({ itemNo: String(rid) });
    setProductPreview({
      retailerId: String(rid),
      name: resolveLocalizedEntityName(row.name, row.nameLocales ?? row.name_locales, currentLanguage) || row.name || '',
      price: listPrice,
      currency: row.currency || 'HKD'
    });
    const curUnit = form.getFieldValue('unitPrice');
    if (curUnit == null || curUnit === '' || Number(curUnit) === 0) {
      if (listPrice != null) form.setFieldsValue({ unitPrice: listPrice });
    }
    setProductPickerOpen(false);
  }, [form, t, currentLanguage]);

  const customerPickerColumns = useMemo(() => [
    {
      title: t('commercePriceCards.customerNo'),
      key: 'customerNo',
      width: 110,
      render: (_, r) => pick(r.customerNo, r.customer_no) || '-'
    },
    {
      title: t('commercePriceCards.branchNo'),
      key: 'branchNo',
      width: 88,
      render: (_, r) => pick(r.branchNo, r.branch_no) || '-'
    },
    {
      title: t('commerceCustomers.customerNameCol'),
      key: 'customerDisplayUi',
      ellipsis: true,
      render: (_, r) => {
        const fromApi = pick(r.displayName, r.DisplayName);
        if (fromApi) return fromApi;
        const n1 = pick(r.name_1, r.name1, r.Name1);
        const n2 = pick(r.name_2, r.name2, r.Name2);
        const n3 = pick(r.name_3, r.name3, r.Name3);
        const nl = parseCustomerNameLocalesRoot(pick(r.name_locales, r.nameLocales, r.NameLocales));
        return resolveCustomerDisplayName(n1, n2, n3, nl, currentLanguage) || '—';
      }
    },
    {
      title: t('commercePriceCards.pickerContactName'),
      key: 'contact',
      width: 100,
      ellipsis: true,
      render: (_, r) => pick(r.contactName, r.contact_name) || '-'
    },
    {
      title: t('commercePriceCards.pickerContactTel'),
      key: 'tel',
      width: 120,
      render: (_, r) => pick(r.tel, r.contact_tel) || '-'
    },
    {
      title: t('commercePriceCards.actions'),
      key: 'pick',
      width: 72,
      fixed: 'right',
      render: (_, row) => (
        <Button type="link" size="small" onClick={() => applyPickedCustomer(row)}>
          {t('commercePriceCards.chooseCustomer')}
        </Button>
      )
    }
  ], [t, applyPickedCustomer, currentLanguage]);

  const productPickerColumns = useMemo(() => [
    {
      title: t('commercePriceCards.itemNo'),
      key: 'rid',
      width: 140,
      ellipsis: true,
      render: (_, r) => pick(r.productRetailerId, r.productRetailer_id) || '-'
    },
    {
      title: t('commercePriceCards.productNameColUiLocale'),
      key: 'name',
      ellipsis: true,
      render: (_, r) =>
        resolveLocalizedEntityName(r.name, r.nameLocales ?? r.name_locales, currentLanguage) || '—'
    },
    {
      title: t('commercePriceCards.productListPrice'),
      key: 'price',
      width: 120,
      render: (_, r) => {
        const price = r.price != null ? Number(r.price) : null;
        const cur = r.currency || 'HKD';
        return price != null ? `${cur} ${price.toFixed(2)}` : '-';
      }
    },
    {
      title: t('commercePriceCards.actions'),
      key: 'pick',
      width: 72,
      fixed: 'right',
      render: (_, row) => (
        <Button type="link" size="small" onClick={() => applyPickedProduct(row)}>
          {t('commercePriceCards.chooseProduct')}
        </Button>
      )
    }
  ], [t, applyPickedProduct, currentLanguage]);

  const showProductPreview = useMemo(() => {
    if (!productPreview || watchedItemNo == null || watchedItemNo === '') return false;
    return String(productPreview.retailerId) === String(watchedItemNo);
  }, [productPreview, watchedItemNo]);

  const openCreate = () => {
    setEditingRecord(null);
    setProductPreview(null);
    form.resetFields();
    form.setFieldsValue({
      showPrice: true,
      companyId: isTenantAdmin ? userInfo.company_id : undefined
    });
    setModalOpen(true);
  };

  const openEdit = useCallback((record) => {
    setEditingRecord(record);
    const companyIdVal = pick(record.companyId, record.company_id);
    form.setFieldsValue({
      companyId: companyIdVal,
      customerNo: record.customerNo,
      branchNo: record.branchNo,
      itemNo: record.itemNo,
      unitPrice: record.unitPrice,
      showPrice: record.showPrice ?? true,
      validFrom: record.validFrom ? dayjs(record.validFrom) : null,
      validTo: record.validTo ? dayjs(record.validTo) : null,
      source: record.source,
      priceType: record.priceType
    });
    setProductPreview(null);
    void loadProductPreviewByItemNo(record.itemNo, companyIdVal);
    setModalOpen(true);
  }, [form, loadProductPreviewByItemNo]);

  const savePriceCard = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        validFrom: values.validFrom ? values.validFrom.format('YYYY-MM-DD') : null,
        validTo: values.validTo ? values.validTo.format('YYYY-MM-DD') : null
      };
      if (!isTenantAdmin) delete payload.companyId;
      setSaving(true);
      const isEdit = !!editingRecord;
      const url = isEdit ? `/api/commerce/price-cards/${editingRecord.id}` : '/api/commerce/price-cards';
      const method = isEdit ? 'PUT' : 'POST';
      const response = await fetchWithAuth(url, {
        method,
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errMsg = await extractResponseError(response);
        message.error(errMsg);
        return;
      }
      message.success(isEdit ? t('commercePriceCards.saved') : t('commercePriceCards.created'));
      setModalOpen(false);
      loadPriceCards();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(`${t('commercePriceCards.saveFailed')}: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }, [form, isTenantAdmin, editingRecord, fetchWithAuth, loadPriceCards, t]);

  const deletePriceCard = useCallback(async (id) => {
    try {
      const response = await fetchWithAuth(`/api/commerce/price-cards/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(await extractResponseError(response));
      message.success(t('commercePriceCards.deleted'));
      loadPriceCards();
    } catch (err) {
      message.error(`${t('commercePriceCards.deleteFailed')}: ${err.message}`);
    }
  }, [fetchWithAuth, loadPriceCards, t]);

  const formatCurrency = (val) => {
    if (val == null || val === '') return '-';
    return `$${Number(val).toFixed(2)}`;
  };

  const columns = useMemo(() => [
    {
      title: t('commercePriceCards.customerNo'),
      dataIndex: 'customerNo',
      key: 'customerNo',
      width: 120
    },
    {
      title: t('commerceCustomers.customerNameCol'),
      dataIndex: 'customerName',
      key: 'customerName',
      width: 160,
      ellipsis: true,
      render: (val) => val || '-'
    },
    {
      title: t('commercePriceCards.branchNo'),
      dataIndex: 'branchNo',
      key: 'branchNo',
      width: 100
    },
    {
      title: t('commercePriceCards.priceType'),
      dataIndex: 'priceType',
      key: 'priceType',
      width: 120,
      ellipsis: true,
      render: (v) => (v != null && v !== '' ? String(v) : '-')
    },
    {
      title: t('commercePriceCards.itemNo'),
      dataIndex: 'itemNo',
      key: 'itemNo',
      width: 140,
      ellipsis: true
    },
    {
      title: t('commercePriceCards.productNameColUiLocale'),
      dataIndex: 'productName',
      key: 'productName',
      width: 180,
      ellipsis: true,
      render: (val) => val || '-'
    },
    {
      title: t('commercePriceCards.unitPrice'),
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 130,
      render: (val) => formatCurrency(val)
    },
    {
      title: t('commercePriceCards.showPrice'),
      dataIndex: 'showPrice',
      key: 'showPrice',
      width: 100,
      render: (val) => (
        <Tag color={val ? 'green' : 'default'}>
          {val ? t('commercePriceCards.yes') : t('commercePriceCards.no')}
        </Tag>
      )
    },
    {
      title: t('commercePriceCards.validFrom'),
      dataIndex: 'validFrom',
      key: 'validFrom',
      width: 130,
      render: (val) => (val ? formatCommerceDateOnly(val, userTimezoneOffset) : '-')
    },
    {
      title: t('commercePriceCards.validTo'),
      dataIndex: 'validTo',
      key: 'validTo',
      width: 130,
      render: (val) => (val ? formatCommerceDateOnly(val, userTimezoneOffset) : '-')
    },
    {
      title: t('commercePriceCards.source'),
      dataIndex: 'source',
      key: 'source',
      width: 140,
      ellipsis: true,
      render: (val) => val || '-'
    },
    {
      title: t('commerceSalesOrders.thirdPartyReadStatus'),
      key: 'is_read_by_3psys',
      width: 120,
      render: (_, record) => {
        const v = pick(record.isReadBy3psys, record.is_read_by_3psys);
        const read = v === true || v === 1 || v === '1';
        return read ? (
          <Tag color="green">{t('commerceSalesOrders.thirdPartyReadDone')}</Tag>
        ) : (
          <Tag color="orange">{t('commerceSalesOrders.thirdPartyReadPending')}</Tag>
        );
      }
    },
    ...buildCommerceAuditColumns(t, userTimezoneOffset),
    {
      title: t('commercePriceCards.actions'),
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space wrap={false} size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            {t('commercePriceCards.edit')}
          </Button>
          <Popconfirm title={t('commercePriceCards.confirmDelete')} onConfirm={() => deletePriceCard(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />}>
              {t('commercePriceCards.delete')}
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ], [t, userTimezoneOffset, openEdit, deletePriceCard]);

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={loadPriceCards}>{t('commercePriceCards.refresh')}</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>{t('commercePriceCards.addPriceCard')}</Button>
            </Space>
            <div style={{ textAlign: 'right' }}>
              <Title level={3} style={{ margin: 0 }}>{t('commercePriceCards.title')}</Title>
              <Text type="secondary">{t('commercePriceCards.description')}</Text>
            </div>
          </div>

          <Space wrap>
            <Input
              placeholder={t('commercePriceCards.filterCustomerNo')}
              allowClear
              value={customerNoInput}
              onChange={(e) => {
                setCustomerNoInput(e.target.value);
                if (!e.target.value) {
                  setCustomerNoFilter('');
                  setListPage(1);
                }
              }}
              onPressEnter={commitSearch}
              style={{ width: 180 }}
            />
            <Input
              placeholder={t('commercePriceCards.filterCustomerName')}
              allowClear
              value={customerNameInput}
              onChange={(e) => {
                setCustomerNameInput(e.target.value);
                if (!e.target.value) {
                  setCustomerNameFilter('');
                  setListPage(1);
                }
              }}
              onPressEnter={commitSearch}
              style={{ width: 200 }}
            />
            <Input
              placeholder={t('commercePriceCards.filterBranchNo')}
              allowClear
              value={branchNoInput}
              onChange={(e) => {
                setBranchNoInput(e.target.value);
                if (!e.target.value) {
                  setBranchNoFilter('');
                  setListPage(1);
                }
              }}
              onPressEnter={commitSearch}
              style={{ width: 180 }}
            />
            <Input
              placeholder={t('commercePriceCards.filterItemNo')}
              allowClear
              value={itemNoInput}
              onChange={(e) => {
                setItemNoInput(e.target.value);
                if (!e.target.value) {
                  setItemNoFilter('');
                  setListPage(1);
                }
              }}
              onPressEnter={commitSearch}
              style={{ width: 180 }}
            />
            <Button icon={<SearchOutlined />} type="primary" onClick={commitSearch} />
            {isTenantAdmin && (
              <Select
                allowClear
                placeholder={t('commercePriceCards.filterByCompany')}
                style={{ width: 260 }}
                value={companyFilter}
                onChange={(v) => {
                  setCompanyFilter(v);
                  setListPage(1);
                }}
                options={companiesForSelect.map(c => ({ label: c.name, value: c.id }))}
              />
            )}
          </Space>

          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={priceCards}
            pagination={commerceServerTablePagination({
              page: listPage,
              pageSize: listPageSize,
              total: priceCardsTotal,
              onChange: (p, ps) => {
                setListPage(p);
                setListPageSize(ps);
              }
            })}
            scroll={{ x: 1320, y: 'calc(100vh - 340px)' }}
          />
        </Space>
      </Card>

      <Modal
        open={modalOpen}
        title={editingRecord ? t('commercePriceCards.editPriceCard') : t('commercePriceCards.addPriceCard')}
        onCancel={() => setModalOpen(false)}
        onOk={savePriceCard}
        confirmLoading={saving}
        width={820}
      >
        <Form form={form} layout="vertical">
          {isTenantAdmin && (
            <Form.Item
              name="companyId"
              label={t('commercePriceCards.company')}
              rules={[{ required: true, message: t('commercePriceCards.required') }]}
            >
              <Select
                placeholder={t('commercePriceCards.selectCompany')}
                options={companiesForSelect.map(c => ({ label: c.name, value: c.id }))}
                showSearch
                optionFilterProp="label"
                filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes((input || '').toLowerCase())}
              />
            </Form.Item>
          )}
          <Form.Item
            label={t('commercePriceCards.customerNo')}
            required
          >
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                name="customerNo"
                noStyle
                rules={[{ required: true, message: t('commercePriceCards.required') }]}
              >
                <Input placeholder={t('commercePriceCards.customerNoPlaceholder')} style={{ width: 'calc(100% - 120px)' }} />
              </Form.Item>
              <Button type="default" icon={<SearchOutlined />} onClick={openCustomerPicker} style={{ width: 120 }}>
                {t('commercePriceCards.openCustomerList')}
              </Button>
            </Space.Compact>
            {editingRecord?.customerName && (
              <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                {t('commercePriceCards.customerName') || 'Customer'}: {editingRecord.customerName}
              </Text>
            )}
          </Form.Item>
          <Form.Item
            name="branchNo"
            label={t('commercePriceCards.branchNo')}
            rules={[{ required: true, message: t('commercePriceCards.required') }]}
          >
            <Input placeholder={t('commercePriceCards.branchNoPlaceholder')} />
          </Form.Item>
          <Form.Item
            label={t('commercePriceCards.itemNo')}
            required
          >
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                name="itemNo"
                noStyle
                rules={[{ required: true, message: t('commercePriceCards.required') }]}
              >
                <Input placeholder={t('commerceProducts.contentId')} style={{ width: 'calc(100% - 120px)' }} />
              </Form.Item>
              <Button type="default" icon={<SearchOutlined />} onClick={openProductPicker} style={{ width: 120 }}>
                {t('commercePriceCards.openProductList')}
              </Button>
            </Space.Compact>
          </Form.Item>
          {showProductPreview ? (
            <div style={{ marginTop: -8, marginBottom: 8 }}>
              <Text type="secondary">
                {t('commercePriceCards.productNameList')}: {productPreview.name || '-'}
              </Text>
              <br />
              <Text type="secondary">
                {t('commercePriceCards.productListPrice')}:
                {productPreview.price != null
                  ? ` ${productPreview.currency} ${Number(productPreview.price).toFixed(2)}`
                  : ' -'}
              </Text>
            </div>
          ) : null}
          <Form.Item
            name="unitPrice"
            label={t('commercePriceCards.unitPrice')}
            rules={[{ required: true, message: t('commercePriceCards.required') }]}
          >
            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="showPrice" label={t('commercePriceCards.showPrice')} valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item
            name="validFrom"
            label={t('commercePriceCards.validFrom')}
            rules={[{ required: true, message: t('commercePriceCards.required') }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="validTo"
            label={t('commercePriceCards.validTo')}
            rules={[{ required: true, message: t('commercePriceCards.required') }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="source" label={t('commercePriceCards.source')}>
            <Input />
          </Form.Item>
          <Form.Item name="priceType" label={t('commercePriceCards.priceType')}>
            <Input placeholder={t('commercePriceCards.priceTypePlaceholder')} allowClear />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('commercePriceCards.customerPickerTitle')}
        open={customerPickerOpen}
        onCancel={() => setCustomerPickerOpen(false)}
        width={960}
        footer={null}
        destroyOnClose
      >
        <Input.Search
          allowClear
          placeholder={t('commercePriceCards.customerPickerSearchPlaceholder')}
          value={customerPickerSearchInput}
          onChange={(e) => setCustomerPickerSearchInput(e.target.value)}
          onSearch={(v) => {
            const q = (v != null ? v : customerPickerSearchInput).trim();
            setCustomerPickerKeyword(q);
            setCustomerPickerPage(1);
          }}
          style={{ marginBottom: 12 }}
          enterButton
        />
        <Table
          rowKey={(r, index) => String(pick(r.id, r.Id) || `c_${customerPickerPage}_${index}`)}
          size="small"
          loading={customerPickerLoading}
          columns={customerPickerColumns}
          dataSource={customerPickerRows}
          scroll={{ x: 820 }}
          pagination={commerceServerTablePagination({
            page: customerPickerPage,
            pageSize: customerPickerPageSize,
            total: customerPickerTotal,
            onChange: (p, ps) => {
              setCustomerPickerPage(p);
              setCustomerPickerPageSize(ps);
            }
          })}
        />
      </Modal>

      <Modal
        title={t('commercePriceCards.productPickerTitle')}
        open={productPickerOpen}
        onCancel={() => setProductPickerOpen(false)}
        width={900}
        footer={null}
        destroyOnClose
      >
        <Input.Search
          allowClear
          placeholder={t('commercePriceCards.productPickerSearchPlaceholder')}
          value={productPickerSearchInput}
          onChange={(e) => setProductPickerSearchInput(e.target.value)}
          onSearch={(v) => {
            const q = (v != null ? v : productPickerSearchInput).trim();
            setProductPickerKeyword(q);
            setProductPickerPage(1);
          }}
          style={{ marginBottom: 12 }}
          enterButton
        />
        <Table
          rowKey={(r, index) => String(r.id || `p_${productPickerPage}_${index}`)}
          size="small"
          loading={productPickerLoading}
          columns={productPickerColumns}
          dataSource={productPickerRows}
          scroll={{ x: 640 }}
          pagination={commerceServerTablePagination({
            page: productPickerPage,
            pageSize: productPickerPageSize,
            total: productPickerTotal,
            onChange: (p, ps) => {
              setProductPickerPage(p);
              setProductPickerPageSize(ps);
            }
          })}
        />
      </Modal>
    </div>
  );
};

export default CommercePriceCardsPage;
